import { memo } from "react";
import type {
  FallbackTier,
  FloodRegionalSnapshot,
  FloodWatchBand,
  FloodWatchSnapshot,
  PcdAirStation,
} from "@kmitl/shared";
import { fmtAge } from "@kmitl/shared";

/** US-AQI banding — same thresholds the AqiBadge uses. */
function aqiColor(aqi: number | null): string {
  if (aqi == null) return "var(--ink-low)";
  if (aqi <= 50) return "var(--good)";
  if (aqi <= 100) return "var(--warn)";
  return "var(--bad)";
}

const BAND_COLOR: Record<FloodWatchBand, string> = {
  normal: "var(--good)",
  watch: "var(--warn)",
  elevated: "var(--bad)",
  critical: "var(--bad)",
};

interface Props {
  floodWatch: FloodWatchSnapshot | null;
  floodLoading: boolean;
  floodAgeMinutes?: number;
  floodFallbackTier?: FallbackTier;
  airStations: PcdAirStation[];
  airAgeMinutes?: number;
  airFallbackTier?: FallbackTier | "loading";
  regional: FloodRegionalSnapshot | null;
  regionalAgeMinutes?: number;
  onFlyTo?: (lng: number, lat: number, zoom?: number) => void;
}

/**
 * Compact resilience strip — campus Flood Watch + PCD air + FloodDash BKK.
 * Feeds are owned by App (single poll); this component only renders.
 */
export const FloodAirHeroStrip = memo(function FloodAirHeroStrip({
  floodWatch,
  floodLoading,
  floodAgeMinutes,
  floodFallbackTier,
  airStations: airStationsIn,
  airAgeMinutes,
  airFallbackTier,
  regional,
  regionalAgeMinutes,
  onFlyTo,
}: Props) {
  const airStations = airStationsIn.slice(0, 3);
  const bkk = regional?.bangkok ?? null;
  const nearestCanal = regional?.canalStations[0] ?? null;

  if (
    floodLoading &&
    airFallbackTier === "loading" &&
    !regional
  ) {
    return null;
  }
  if (!floodWatch && airStations.length === 0 && !bkk) {
    return null;
  }

  const floodAgeLabel =
    floodFallbackTier === "live" || (floodAgeMinutes != null && floodAgeMinutes < 1)
      ? "LIVE"
      : fmtAge(floodAgeMinutes);

  return (
    <section
      className="flood-air-hero"
      aria-label="Campus resilience: flood watch, official PCD air quality, regional flood risk"
    >
      <div className="flood-air-hero__cell flood-air-hero__cell--flood">
        <span className="mono eyebrow flood-air-hero__tag">FLOOD WATCH</span>
        {floodLoading && !floodWatch ? (
          <span className="caption mono flood-air-hero__muted">connecting…</span>
        ) : !floodWatch ? (
          <span className="caption mono flood-air-hero__muted">unavailable</span>
        ) : (
          <button
            type="button"
            className="flood-air-hero__flood-btn"
            onClick={() => {
              const station = floodWatch.waterStations[0] ?? floodWatch.rainStations[0];
              if (station) onFlyTo?.(station.lng, station.lat, 13);
            }}
            title="Fly to nearest water/rain station"
          >
            <span
              className="flood-air-hero__score"
              style={{ color: BAND_COLOR[floodWatch.band] }}
            >
              <span className="mono flood-air-hero__num">{floodWatch.score}</span>
              <span className="mono flood-air-hero__den">/100</span>
            </span>
            <span className="flood-air-hero__band">
              <span
                className="mono eyebrow"
                style={{ color: BAND_COLOR[floodWatch.band] }}
              >
                {floodWatch.labelEn.toUpperCase()}
              </span>
              <span className="caption flood-air-hero__th">{floodWatch.labelTh}</span>
            </span>
          </button>
        )}
      </div>

      <div className="flood-air-hero__rule" aria-hidden />

      <div className="flood-air-hero__cell flood-air-hero__cell--air">
        <span className="mono eyebrow flood-air-hero__tag">PCD AIR · AIR4THAI</span>
        {airStations.length === 0 ? (
          <span className="caption mono flood-air-hero__muted">no stations</span>
        ) : (
          <div className="flood-air-hero__air-list">
            {airStations.map((s) => (
              <button
                key={s.stationKey}
                type="button"
                className="flood-air-hero__air-btn"
                onClick={() => onFlyTo?.(s.lng, s.lat, 14)}
                title={[s.nameTh, s.addressEn].filter(Boolean).join(" · ") + " — fly to station"}
              >
                <span
                  className="mono flood-air-hero__air-aqi"
                  style={{ color: aqiColor(s.aqi) }}
                >
                  {s.aqi ?? "—"}
                </span>
                <span className="flood-air-hero__air-name">{s.nameEn}</span>
                <span className="mono caption flood-air-hero__air-meta">
                  {s.pm25 != null ? `${s.pm25.toFixed(0)} µg` : "—"} · {s.distanceKm.toFixed(1)} km
                </span>
              </button>
            ))}
          </div>
        )}
      </div>

      <div className="flood-air-hero__rule" aria-hidden />

      <div className="flood-air-hero__cell flood-air-hero__cell--regional">
        <span className="mono eyebrow flood-air-hero__tag">FLOODDASH · BKK</span>
        {!bkk && !regional?.national ? (
          <span className="caption mono flood-air-hero__muted">no regional card</span>
        ) : (
          <div className="flood-air-hero__regional">
            {bkk && (
              <button
                type="button"
                className="flood-air-hero__bkk"
                onClick={() => {
                  if (nearestCanal) onFlyTo?.(nearestCanal.lng, nearestCanal.lat, 13);
                }}
                disabled={!nearestCanal}
                title={
                  nearestCanal
                    ? `Fly to nearest canal gauge · ${nearestCanal.nameEn || nearestCanal.nameTh}`
                    : "Bangkok FloodDash risk card"
                }
              >
                <span
                  className="mono flood-air-hero__bkk-score"
                  style={{ color: BAND_COLOR[bkk.band] }}
                >
                  {bkk.score}
                  <span className="flood-air-hero__bkk-den">/100</span>
                </span>
                <span
                  className="mono caption flood-air-hero__bkk-band"
                  style={{ color: BAND_COLOR[bkk.band] }}
                >
                  {bkk.band.toUpperCase()}
                </span>
                {bkk.maxRain24h != null && bkk.maxRain24h > 0 && (
                  <span className="mono caption flood-air-hero__bkk-meta">
                    max rain {bkk.maxRain24h.toFixed(1)} mm/24h
                  </span>
                )}
                {bkk.stationsL4 + bkk.stationsL5 > 0 && (
                  <span className="mono caption flood-air-hero__bkk-meta flood-air-hero__bkk-meta--alert">
                    {bkk.stationsL4 + bkk.stationsL5} gauges high
                  </span>
                )}
                {nearestCanal && (
                  <span className="mono caption flood-air-hero__bkk-meta">
                    {(nearestCanal.nameEn || nearestCanal.nameTh)} · {nearestCanal.distanceKm} km
                    {nearestCanal.storagePct != null
                      ? ` · ${Math.round(nearestCanal.storagePct)}%`
                      : ""}
                  </span>
                )}
              </button>
            )}
            {regional?.national && (
              <div className="flood-air-hero__national">
                <span className="mono caption flood-air-hero__muted">
                  TH {regional.national.band.toUpperCase()}
                </span>
                {regional.national.soilSaturationPct != null && (
                  <span className="mono caption flood-air-hero__muted">
                    soil {regional.national.soilSaturationPct}%
                  </span>
                )}
                {regional.national.worstRainProvinceEn && regional.national.worstRainMm != null && (
                  <span className="mono caption flood-air-hero__muted">
                    peak {regional.national.worstRainProvinceEn}{" "}
                    {regional.national.worstRainMm.toFixed(0)} mm
                  </span>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      <p className="flood-air-hero__foot mono caption">
        {floodWatch && floodAgeMinutes != null && (
          <span>flood · {floodAgeLabel} · hii-thaiwater</span>
        )}
        {airStations.length > 0 && (
          <span>air · air4thai · {fmtAge(airAgeMinutes)}</span>
        )}
        {bkk && (
          <span>
            bkk · FloodDash · {fmtAge(regionalAgeMinutes)} · not a forecast
          </span>
        )}
      </p>
    </section>
  );
});
